import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Star } from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import { values } from "./data";

const ValuesCarousel = () => {
    const [activeValue, setActiveValue] = useState(0);
    const [autoplay, setAutoplay] = useState(true);

    useEffect(() => {
        if (!autoplay) return;

        const interval = setInterval(() => {
            setActiveValue((prev) => (prev + 1) % values.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [autoplay]);

    return (
        <section className="relative py-20">
            <div className="absolute inset-0">
                {[...Array(40)].map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{
                            y: [0, -100, 0],
                            x: [0, Math.sin(i) * 50, 0],
                            opacity: [0.1, 0.3, 0.1],
                        }}
                        transition={{
                            duration: 10 + i,
                            repeat: Infinity,
                            delay: i * 0.5,
                            ease: "linear"
                        }}
                        className="absolute w-32 h-32 rounded-full blur-xl bg-gradient-to-r from-blue-500/5 to-cyan-500/5"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4">
                <AnimatedSection delay={0.2}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6">
                            Nos<span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">Valeurs</span>
                        </h2>

                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Les principes qui guident chacune de nos actions et décisions
                        </p>
                    </div>
                </AnimatedSection>

                <AnimatedSection delay={0.3}>
                    <div className="relative mb-16">
                        <div className="max-w-2xl mx-auto">
                            <motion.div
                                key={activeValue}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="relative group"
                            >
                                <div className="absolute -inset-6 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300" />

                                <div className="relative bg-gray-900/50 backdrop-blur-xl rounded-2xl p-8 border border-gray-700 shadow-xl">
                                    <div className="flex flex-col items-center text-center">
                                        <div className={`w-24 h-24 rounded-2xl bg-gradient-to-r ${values[activeValue].color} flex items-center justify-center mb-8`}>
                                            {React.createElement(values[activeValue].icon, { className: "w-12 h-12 text-white" })}
                                        </div>

                                        <h3 className="text-3xl font-bold text-white mb-4">{values[activeValue].title}</h3>
                                        <p className="text-lg text-gray-300 max-w-xl">{values[activeValue].description}</p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </AnimatedSection>

                <AnimatedSection delay={0.4}>
                    <div className="relative">
                        <div className="relative">
                            {/* <div className="absolute left-0 right-0 top-1/2 h-px bg-gray-700 -translate-y-1/2" /> */}

                            <div className="relative flex justify-between items-center px-4">
                                <button
                                    onClick={() => {
                                        setActiveValue((prev) => (prev - 1 + values.length) % values.length);
                                        setAutoplay(false);
                                    }}
                                    className="z-10 shrink-0 w-10 h-10 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center hover:bg-gray-700 transition-colors shadow-sm cursor-pointer"
                                >
                                    <ChevronRight className="w-5 h-5 rotate-180 text-gray-300" />
                                </button>

                                <div className="flex-1 overflow-x-auto lg:overflow-visible no-scrollbar mx-2">
                                    <div className="relative flex items-center justify-center space-x-4 md:space-x-8 px-4 py-2 min-w-max lg:min-w-0">
                                        {values.map((value, index) => {
                                            const isActive = activeValue === index;

                                            return (
                                                <motion.button
                                                    key={index}
                                                    onClick={() => {
                                                        setActiveValue(index);
                                                        setAutoplay(false);
                                                    }}
                                                    className="relative cursor-pointer group"
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.95 }}
                                                >
                                                    <div className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${isActive
                                                        ? `bg-gradient-to-r ${value.color} scale-110 shadow-lg`
                                                        : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                                                        }`}>
                                                        {React.createElement(value.icon, { className: `w-6 h-6 transition-colors ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'}` })}

                                                        {isActive && (
                                                            <motion.div
                                                                initial={{ scale: 1, opacity: 0.5 }}
                                                                animate={{ scale: 1.5, opacity: 0 }}
                                                                transition={{ duration: 2, repeat: Infinity }}
                                                                className="absolute inset-0 rounded-full border-2 border-current opacity-50"
                                                                style={{
                                                                    color: value.color.includes('blue') ? '#3b82f6' :
                                                                        value.color.includes('red') ? '#ef4444' :
                                                                            value.color.includes('emerald') ? '#10b981' :
                                                                                value.color.includes('purple') ? '#8b5cf6' :
                                                                                    value.color.includes('amber') ? '#f59e0b' : '#06b6d4'
                                                                }}
                                                            />
                                                        )}
                                                    </div>

                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                                        <div className="bg-gray-800 text-white text-xs font-medium px-2 py-1 rounded whitespace-nowrap">
                                                            {value.title}
                                                        </div>
                                                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800" />
                                                    </div>
                                                </motion.button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <button
                                    onClick={() => {
                                        setActiveValue((prev) => (prev + 1) % values.length);
                                        setAutoplay(false);
                                    }}
                                    className="z-10 shrink-0 w-10 h-10 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center hover:bg-gray-700 transition-colors shadow-sm cursor-pointer"
                                >
                                    <ChevronRight className="w-5 h-5 text-gray-300" />
                                </button>
                            </div>

                            <div className="flex justify-center gap-4 mt-8">
                                {values.map((_, index) => (

                                    <button
                                        key={index}
                                        onClick={() => {
                                            setActiveValue(index);
                                            setAutoplay(false);
                                        }}
                                        className={`w-3 h-3 rounded-full transition-all cursor-pointer duration-300 ${activeValue === index
                                            ? "bg-blue-500 scale-125"
                                            : "bg-gray-700 hover:bg-gray-600"
                                            }`}
                                        aria-label={`Valeur ${index + 1}`}
                                    />

                                ))}

                            </div>

                        </div>

                    </div>

                </AnimatedSection>

                <AnimatedSection delay={0.5}>

                    <div className="mt-20 text-center">

                        <div className="relative max-w-3xl mx-auto">

                            <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-7 border border-gray-700 shadow-lg">

                                <Star className="w-12 h-12 text-blue-500/30 mx-auto mb-6" />
                                <p className="text-2xl text-gray-200 italic mb-6 leading-relaxed">
                                    "Ces valeurs ne sont pas seulement des mots, mais le fondement même de notre culture
                                    d'entreprise et la boussole qui guide chaque décision que nous prenons."
                                </p>
                                <div className="h-px w-24 bg-gradient-to-r from-blue-500 to-emerald-500 mx-auto mb-4" />
                                <span className="text-gray-400 font-medium">Notre engagement quotidien</span>

                            </div>

                        </div>

                    </div>

                </AnimatedSection>

            </div >

        </section >
    );
};

export default ValuesCarousel;
